import { useEffect, useState } from "react";
import axios from "axios";
import AccountSkeleton from "../Components/AccountSkeleton";
import toast from "react-hot-toast";
import SubscriptionStatus from "../Components/SubscriptionStatus";
import UserDetails from "../Components/UserDetails";
import CompanyDetails from "../Components/CompanyDetails";
import api from "../Services/api";
import { authService } from "../Services/Auth";

interface Subscription {
  id: string;
  userId: string;
  tier: string;
  startDate: string;
  nextPaymentDate: string;
  classificationLimit: number;
  classificationsUsed: number;
  status: string;
  interval: string;
}

interface User {
  email: string;
  firstName: string;
  lastName: string;
  businessName?: string;
  phoneNumber: string;
  streetAddress: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  companyName?: string;
  companyTitle?: string;
  companyPhoneNumber?: string;
  companyStreetAddress?: string;
  companyCity?: string;
  companyState?: string;
  companyCountry?: string;
  companyPostalCode?: string;
}

export default function Account() {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [isPasswordResetLoading, setIsPasswordResetLoading] = useState(false);

  // Edit states and corresponding fields for user details
  const [isEditingStreetAddress, setIsEditingStreetAddress] = useState(false);
  const [isEditingCity, setIsEditingCity] = useState(false);
  const [isEditingState, setIsEditingState] = useState(false);
  const [isEditingCountry, setIsEditingCountry] = useState(false);
  const [isEditingPostalCode, setIsEditingPostalCode] = useState(false);
  const [streetAddress, setStreetAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");
  const [postalCode, setPostalCode] = useState("");

  // Edit states and corresponding fields for company details
  const [isEditingCompanyName, setIsEditingCompanyName] = useState(false);
  const [isEditingCompanyTitle, setIsEditingCompanyTitle] = useState(false);
  const [isEditingCompanyPhoneNumber, setIsEditingCompanyPhoneNumber] = useState(false);
  const [isEditingCompanyStreetAddress, setIsEditingCompanyStreetAddress] = useState(false);
  const [isEditingCompanyCity, setIsEditingCompanyCity] = useState(false);
  const [isEditingCompanyState, setIsEditingCompanyState] = useState(false);
  const [isEditingCompanyCountry, setIsEditingCompanyCountry] = useState(false);
  const [isEditingCompanyPostalCode, setIsEditingCompanyPostalCode] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [companyTitle, setCompanyTitle] = useState("");
  const [companyPhoneNumber, setCompanyPhoneNumber] = useState("");
  const [companyStreetAddress, setCompanyStreetAddress] = useState("");
  const [companyCity, setCompanyCity] = useState("");
  const [companyState, setCompanyState] = useState("");
  const [companyCountry, setCompanyCountry] = useState("");
  const [companyPostalCode, setCompanyPostalCode] = useState("");

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const canceled = urlParams.get('canceled');

    if (sessionId) {
      toast.success('Subscription activated successfully!');
      fetchSubscriptionData();
    } else if (canceled) {
      toast('Checkout was canceled. You can try again anytime.');
    }

    // Clean up URL parameters
    if (sessionId || canceled) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);


  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");
        const [subscriptionResponse, userResponse] = await Promise.all([
          api.get("/subscription/status", { headers: { Authorization: `Bearer ${token}` } }),
          api.get("/auth/user", { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        setSubscription(subscriptionResponse.data);
        setUser(userResponse.data);
        if (userResponse.data) {
          // Set user details
          setStreetAddress(userResponse.data.streetAddress || "");
          setCity(userResponse.data.city || "");
          setState(userResponse.data.state || "");
          setCountry(userResponse.data.country || "");
          setPostalCode(userResponse.data.postalCode || "");

          // Set company details
          setCompanyName(userResponse.data.companyName || "");
          setCompanyTitle(userResponse.data.companyTitle || "");
          setCompanyPhoneNumber(userResponse.data.companyPhoneNumber || "");
          setCompanyStreetAddress(userResponse.data.companyStreetAddress || "");
          setCompanyCity(userResponse.data.companyCity || "");
          setCompanyState(userResponse.data.companyState || "");
          setCompanyCountry(userResponse.data.companyCountry || "");
          setCompanyPostalCode(userResponse.data.companyPostalCode || "");
        }
      } catch (error: any) {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          localStorage.removeItem("token");
          window.location.href = "/auth";
        }
        if (error.response?.data?.error === "User not found") {
          localStorage.removeItem("token");
          window.location.href = "/auth";
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const fetchSubscriptionData = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get("/subscription/status", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscription(response.data);
    } catch (error) {
      console.error("Error fetching subscription data:", error);
    }
  };


  const handleSave = async (field: string, value: string) => {
    try {
      const token = localStorage.getItem("token");
      await api.patch("/auth/user", { [field]: value }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update the user state with the new value
      setUser(prev => prev ? { ...prev, [field]: value } : null);

      toast.success("Updated successfully!");
    } catch (error) {
      toast.error("Failed to update. Please try again.");
    }
  };

  const refreshSubscription = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get("/subscription/status", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscription(response.data);
      toast.success("Subscription status updated!");
    } catch (error) {
      toast.error("Failed to refresh subscription status");
    }
  };

  const handleManageSubscription = async () => {
    try {
      toast.loading("Opening billing portal...");

      const token = localStorage.getItem("token");
      const { data } = await api.post("/stripe/portal", null, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.dismiss();
      window.location.href = data.url;
    } catch (error) {
      toast.dismiss();
      toast.error("Failed to open billing portal. Please try again.");
      console.error("Error accessing billing portal:", error);
    }
  };

  const handleRenewal = () => {
    window.location.href = "/dashboard";
  };

  const handleRequestPasswordChange = async () => {
    try {
      setIsPasswordResetLoading(true);
      await authService.requestPasswordChange();
      toast.success("Password reset email sent. Please check your inbox.");
    } catch (error) {
      toast.error("Failed to send password reset email. Please try again.");
    } finally {
      setIsPasswordResetLoading(false);
    }
  };

  if (loading) {
    return <AccountSkeleton />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="py-12 px-4 sm:px-6 lg:px-8 text-gray-900 dark:text-gray-100">
        <h1 className="text-3xl font-bold">Account Settings</h1>

        {subscription && subscription.status && (
          <SubscriptionStatus
            subscription={subscription}
            onManageSubscription={handleManageSubscription}
            onRenewal={handleRenewal}
          />
        )}

        {user && (
          <UserDetails
            user={user}
            isEditingStreetAddress={isEditingStreetAddress}
            isEditingCity={isEditingCity}
            isEditingState={isEditingState}
            isEditingCountry={isEditingCountry}
            isEditingPostalCode={isEditingPostalCode}
            streetAddress={streetAddress}
            city={city}
            state={state}
            country={country}
            postalCode={postalCode}
            onStreetAddressChange={(e) => setStreetAddress(e.target.value)}
            onCityChange={(e) => setCity(e.target.value)}
            onStateChange={(e) => setState(e.target.value)}
            onCountryChange={(e) => setCountry(e.target.value)}
            onPostalCodeChange={(e) => setPostalCode(e.target.value)}
            onStreetAddressEdit={() => setIsEditingStreetAddress(!isEditingStreetAddress)}
            onCityEdit={() => setIsEditingCity(!isEditingCity)}
            onStateEdit={() => setIsEditingState(!isEditingState)}
            onCountryEdit={() => setIsEditingCountry(!isEditingCountry)}
            onPostalCodeEdit={() => setIsEditingPostalCode(!isEditingPostalCode)}
            onSave={handleSave}
          />
        )}

        {user && (
          <CompanyDetails
            user={user}
            isEditingCompanyName={isEditingCompanyName}
            isEditingCompanyTitle={isEditingCompanyTitle}
            isEditingCompanyPhoneNumber={isEditingCompanyPhoneNumber}
            isEditingCompanyStreetAddress={isEditingCompanyStreetAddress}
            isEditingCompanyCity={isEditingCompanyCity}
            isEditingCompanyState={isEditingCompanyState}
            isEditingCompanyCountry={isEditingCompanyCountry}
            isEditingCompanyPostalCode={isEditingCompanyPostalCode}
            companyName={companyName}
            companyTitle={companyTitle}
            companyPhoneNumber={companyPhoneNumber}
            companyStreetAddress={companyStreetAddress}
            companyCity={companyCity}
            companyState={companyState}
            companyCountry={companyCountry}
            companyPostalCode={companyPostalCode}
            onCompanyNameChange={(e) => setCompanyName(e.target.value)}
            onCompanyTitleChange={(e) => setCompanyTitle(e.target.value)}
            onCompanyPhoneNumberChange={(e) => setCompanyPhoneNumber(e.target.value)}
            onCompanyStreetAddressChange={(e) => setCompanyStreetAddress(e.target.value)}
            onCompanyCityChange={(e) => setCompanyCity(e.target.value)}
            onCompanyStateChange={(e) => setCompanyState(e.target.value)}
            onCompanyCountryChange={(e) => setCompanyCountry(e.target.value)}
            onCompanyPostalCodeChange={(e) => setCompanyPostalCode(e.target.value)}
            onCompanyNameEdit={() => setIsEditingCompanyName(!isEditingCompanyName)}
            onCompanyTitleEdit={() => setIsEditingCompanyTitle(!isEditingCompanyTitle)}
            onCompanyPhoneNumberEdit={() => setIsEditingCompanyPhoneNumber(!isEditingCompanyPhoneNumber)}
            onCompanyStreetAddressEdit={() => setIsEditingCompanyStreetAddress(!isEditingCompanyStreetAddress)}
            onCompanyCityEdit={() => setIsEditingCompanyCity(!isEditingCompanyCity)}
            onCompanyStateEdit={() => setIsEditingCompanyState(!isEditingCompanyState)}
            onCompanyCountryEdit={() => setIsEditingCompanyCountry(!isEditingCompanyCountry)}
            onCompanyPostalCodeEdit={() => setIsEditingCompanyPostalCode(!isEditingCompanyPostalCode)}
            onSave={handleSave}
          />
        )}

        {/* Security Section */}
        <div style={{ maxWidth: "500px" }} className="mt-8 bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Security</h2>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-center">
                <label className="text-sm text-gray-500">Request Password Change</label>
                <button
                  onClick={handleRequestPasswordChange}
                  disabled={isPasswordResetLoading}
                  className="px-4 py-2 bg-secondary dark:bg-secondary hover:bg-secondary-dark dark:hover:bg-secondary-dark text-white rounded-lg transition-colors text-sm"
                >
                  {isPasswordResetLoading ? "Sending..." : "Change Password"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
